/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 9 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useEffect, useState, lazy, Suspense, createElement, useCallback } from "react";
import { Route, useLocation, useNavigate, Routes, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { getCheckIfAuthenticated, getUserDetails } from "./actions/userDetails";
import { hideChatBot, hideRecaptchaBadge, showChatBot } from "@Services/utils/helpers";
import LicenceWarning from "./components/LicenceWarning/LicenceWarning";
import { getPortalSetting, getUserPermissions } from "./actions/userPermissions";
import { fetchLayout } from "./actions/layout";
import MinimizeFloatter from "@Components/MinimizeFloatter";
import { getPortalData } from "@Actions/portalSetting";
import * as Sentry from "@sentry/react";
import { identifyPostHog } from "./services/posthog";
import OldBrowserWarning from "@Components/OldBrowserWarning";
import Notification from "./components/Notification";
import MobileDeviceRestrict from "@Components/MobileDeviceRestrict";
import { getCurrProjectData } from "./actions/breadcrumbsdata";
import axiosWrapper from "@AxiosWrapper";
// import { setHelpSectionState } from "./reducers/portalDashboard";
import StickySidebar from "./components/StickySidebar";
import { setShowStickySidebar, setSidebarSelectedCell } from "./reducers/stickySidebar";
import ErrorBoundary from "./ErrorBoundary";
import routes from "./routes";
import { getPortalCustomModules } from "@Actions/portalSetting.helper";
import { setPreferredLanguage } from "./reducers/preferredLanguage";
import _ from "lodash";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
// import HelpSection from "./HelpSection";
import { setRenderHeader } from "./reducers/portalSetting";
import { addRedirectParam } from "@Helpers/urlHelpers";
import { setSigninData } from "./reducers/signinData";
import { LINKING_ACCOUNT_PATH } from "./components/SignupFlow/linkAccountHandoff";
import type {
  GetSigninDataResponse,
  GetSleekTokenResponse,
  GetExtendedResponse,
} from "@ServerTypes/accounts/accounts.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import Text from "@CubeComponents/html/Text/Text";
import Forbidden from "@Pages/Error/Forbidden";
import { CircularProgress } from "@mui/material";
import { RootState } from "@Reducers/index";
import { PORTAL_NO_SEAT } from "@Helpers/constants";
import { setBreadCrumbData, setProjectData } from "@Reducers/breadcrumbsdata";
import { t } from "@i18n";

const FeedbackToastComponent = lazy(() => import("./cubeComponent/html/feedbackToast"));
const FileSync = lazy(() => import("@Features/files/components/FilesSync"));
const PortalIllustration = lazy(() =>
  import("@Features/portal").then((module) => ({ default: module.PortalIllustration })),
);
const Header = lazy(() => import("@Components/Header"));
const HelpChat = lazy(() => import("@Components/HelpChat"));

const FileUpload = lazy(() => import("@Features/files/components/FileManager/FileUpload/FileUpload"));

function Router() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const licenceStatus = useSelector((state: RootState) => state.licenceStatus);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  // fileSyncState,
  const showStickySidebar = useSelector((state: RootState) => state.showStickySidebar);

  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const helpSectionState = useSelector((state: RootState) => state.helpSectionState);
  const renderHeader = useSelector((state: RootState) => state.renderHeader);
  const portalData = useSelector((state: RootState) => state.portalData);
  const checkIfAuthenticated = useSelector((state: RootState) => state.checkIfAuthenticated);
  const signinData = useSelector((state: RootState) => state.signinData);
  const projectData = useSelector((state: RootState) => state.projectData);

  const [isPortalExpired, setIsPortalExpired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showLogoutError, setShowLogoutError] = useState("");
  const [showOldBrowserWarning, setShowOldBrowserWarning] = useState(false);
  // const [isDragging, setIsDragging] = useState(false);
  // const [splitterSize, setSplitterSize] = useState([100, 0]);
  const detectMobileDevice = () => {
    if (!globalThis.location.host.includes("dpmsportal")) {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
        !globalThis.location.href.includes("mobileExplorer") &&
        !globalThis.location.href.includes("signupflow") &&
        !globalThis.location.href.includes("newSignIn") &&
        !globalThis.location.href.includes("reset-password") &&
        !globalThis.location.href.includes("newInvitation") &&
        // Shareable external links (CP-283): a recipient has no CUBE account and no app to install —
        // the share pages ARE the experience, so the "use the app" screen would leave them nothing.
        !globalThis.location.pathname.startsWith("/share/") &&
        globalThis.location.pathname !== "/"
      ) {
        setIsMobileDevice(true);
      } else {
        setIsMobileDevice(false);
      }
    }
  };

  const errorMessageParam = searchParams.get("errorMessage");
  useEffect(() => {
    const errorMessage = searchParams.get("errorMessage");
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("errorMessage");

    if (errorMessage) {
      setSearchParams(newSearchParams);
      setShowLogoutError(decodeURIComponent(errorMessage));
    }
  }, [errorMessageParam]);

  const projectIdParam = searchParams.get("projectId");
  useEffect(() => {
    // URL query params enter from outside the type system, so the id is typed once here.
    const projectId = searchParams.get("projectId") as ObjectIdString | null;
    if (projectId) {
      dispatch(getCurrProjectData({ id: projectId, originSource: "PROJECT" }));
    } else {
      dispatch(setProjectData({ isDataFetched: false }));
    }
  }, [projectIdParam]);

  useEffect(() => {
    const isProjectScopedRoute = location.pathname.startsWith("/project");
    if (!projectData._id && isProjectScopedRoute) {
      const breadCrumb = [
        {
          text: t("Projects"),
          link: "/projects",
        },
      ];
      dispatch(setBreadCrumbData(breadCrumb));
    }
  }, [projectData._id, location.pathname]);

  useEffect(() => {
    validateReturnRouter();
  }, [portalData, location.pathname, userDetails.id]);

  const validateReturnRouter = async () => {
    if (userDetails.id) {
      if (
        portalData.isExpired &&
        !location.pathname.includes("signupflow") &&
        location.pathname != "/newInvitation" &&
        userDetails.portal?.id
      ) {
        setIsPortalExpired(true);
      } else if (!userDetails.portal?.id && !["/", "/signupflow", "/newInvitation"].includes(location.pathname)) {
        const signinData = await axiosWrapper.post<GetSigninDataResponse>(
          `${getBackendUrl()}/accounts/getSigninData`,
          {},
        );
        if (globalThis.location.host != signinData.data.frontendUrl) {
          const newUrl = addRedirectParam({
            targetPath: new URL("/", globalThis.location.origin),
            // `AddRedirectParamOptions.redirectTo` is declared `Location | URL`, meaning the DOM `Location`,

            // not react-router's. Only pathname/search/hash are read, so the intersection is erased and safe.

            redirectTo: location as typeof location & URL,
          });
          navigate(newUrl);
        }
      } else {
        const planLineItems = portalData.subscription?.lineItems ?? [];
        // `code` is optional on the subscription line-item schema but always set on a plan row.
        const planCodes: string[] = planLineItems.filter((i) => i.type === "plan").map((i) => i.code!);
        if (!planCodes.length) {
          const fallback = portalData.subscription?.plan ?? "";
          if (fallback) planCodes.push(fallback);
        }
        const seat = planCodes
          .map((code: string) => _.get(userDetails, `seat.${code}`))
          .find((s: unknown) => s !== undefined && s !== null && s !== PORTAL_NO_SEAT);
        const portalRole = _.get(userDetails, "portalRole.id");
        if (portalData.newSignUpFlow && !seat && portalRole) {
          setIsPortalExpired(true);
        } else {
          setIsPortalExpired(false);
        }
      }
    } else {
      setIsPortalExpired(false);
    }
  };

  const initSleekPlan = async () => {
    const getSleekToken = await axiosWrapper.get<GetSleekTokenResponse>(
      `${getBackendUrl()}/accounts/getSleekToken`,
      {},
    );
    if (getSleekToken.data.status) {
      // Erased assertion, no runtime change: we are inside `if (getSleekToken.data.status)`, and the
      // endpoint only reports success with a token. `_.get`'s string-path overload cannot know that.
      const token = _.get(getSleekToken, "data.data.token")!;
      if (window.SLEEK_LOADED) {
        if (typeof window.$sleek?.setUser === "function") {
          window.$sleek.setUser({ token });
        }
        return;
      }

      window.$sleek = [];
      window.SLEEK_PRODUCT_ID = 401112407;

      const script = document.createElement("script");
      script.src = "https://client.sleekplan.com/sdk/e.js";
      script.async = true;

      document.head.appendChild(script);
      window.SLEEK_LOADED = true;

      window.document.addEventListener(
        "sleek:init",
        () => {
          if (typeof window.$sleek?.setUser === "function") {
            window.$sleek.setUser({ token });
          }
        },
        { once: true },
      );
    }
  };

  useEffect(() => {
    if (location.pathname.includes("null") || location.search.includes("null")) {
      globalThis.location.href = "/";
    }
  }, [location]);

  const getSigninData = useCallback(async () => {
    const signinData = await axiosWrapper.post<GetSigninDataResponse>(`${getBackendUrl()}/accounts/getSigninData`, {});
    dispatch(setSigninData({ data: signinData.data }));
  }, []);

  useEffect(() => {
    dispatch(getCheckIfAuthenticated());
    getSigninData();
  }, [getSigninData]);

  useEffect(() => {
    if (userDetails.id && portalData._id && userDetails.portal?.id) {
      dispatch(getCheckIfAuthenticated());
      dispatch(getPortalSetting());
    }
  }, [userDetails.id, portalData._id, userDetails.portal?.id]);

  const checkIfAuthenticatedStatus = _.get(checkIfAuthenticated, "data.status");

  useEffect(() => {
    if (checkIfAuthenticated !== null) {
      if (_.get(checkIfAuthenticated, "data.status")) {
        dispatch(
          getUserPermissions({
            resourceType: ["RESOURCE_TYPE_PORTAL"],
          }),
        );
        hideRecaptchaBadge();
        hideChatBot();
      } else {
        setIsPortalExpired(false);
        showChatBot();
      }
    }
  }, [checkIfAuthenticatedStatus]);

  const userDetailsPortalId = _.get(userDetails, "portal.id");
  useEffect(() => {
    if (_.get(checkIfAuthenticated, "data.status") && (!userDetails.id || !userDetails.portal?.id)) {
      dispatch(getUserDetails());
    }
  }, [checkIfAuthenticatedStatus, userDetails.id, userDetailsPortalId]);

  useEffect(() => {
    if (userDetails.id && portalData._id && userDetails.portal?.id) {
      dispatch(getPortalCustomModules());
    }
  }, [userDetails.id, portalData._id, userDetails.portal?.id]);

  useEffect(() => {
    if (userDetails.id) {
      if (process.env.REACT_APP_SENTRY_DSN) {
        Sentry.setUser({ email: userDetails.email, id: userDetails.id });
      }
      identifyPostHog(userDetails);
      // if cypress then do not call
      // `Cypress` is injected by the Cypress runner at runtime and has no ambient declaration in
      // this package. Asserted rather than declared globally so the fake stays local to its use.
      if (
        !(globalThis as typeof globalThis & { Cypress?: unknown }).Cypress &&
        !globalThis.location.pathname.includes("pdfExport")
      ) {
        void initSleekPlan();
      }
    }
  }, [userDetails]);

  const checkBrowserCompatibility = () => {
    if (typeof Array.prototype.at != "function" || typeof Array.prototype.toSorted != "function") {
      setShowOldBrowserWarning(true);
    }
  };
  useEffect(() => {
    if (
      _.get(checkIfAuthenticated, "data.status") &&
      globalThis.location.host == _.get(signinData, "data.frontendUrl") &&
      signinData?.data?.EnableMultiPortal &&
      !["/signupflow", "/reset-password", "/", LINKING_ACCOUNT_PATH].includes(location.pathname)
    ) {
      const newUrl = addRedirectParam({
        targetPath: new URL("/", globalThis.location.origin),
        // `AddRedirectParamOptions.redirectTo` is declared `Location | URL`, meaning the DOM `Location`,

        // not react-router's. Only pathname/search/hash are read, so the intersection is erased and safe.

        redirectTo: location as typeof location & URL,
      });
      navigate(newUrl);
    }
  }, [checkIfAuthenticatedStatus, signinData, location.pathname]);

  const updateLanguage = useCallback(async () => {
    const response = await axiosWrapper.post<GetExtendedResponse>(`${getBackendUrl()}/accounts/get`, {
      projection: ["preferredLanguage"],
    });
    const newLanguage = _.get(response, "data.data.preferredLanguage") ?? "en";
    dispatch(setPreferredLanguage(newLanguage));
    if (["ar", "he", "fa", "ur"].includes(newLanguage)) {
      document.body.setAttribute("dir", "rtl");
    }
  }, []);

  useEffect(() => {
    if (_.get(checkIfAuthenticated, "data.status")) {
      dispatch(fetchLayout());
      updateLanguage();
    } else if (checkIfAuthenticated !== null && !_.get(checkIfAuthenticated, "data.status")) {
      dispatch(setPreferredLanguage("en"));
    }
  }, [checkIfAuthenticatedStatus, updateLanguage]);

  useEffect(() => {
    checkBrowserCompatibility();
  }, []);

  useEffect(() => {
    detectMobileDevice();
  }, [location]);

  useEffect(() => {
    if (location.pathname != "/") {
      setShowLogoutError("");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes("googleSigninButton")) {
      dispatch(getPortalData());
    }
  }, [location.pathname]);

  useEffect(() => {
    if (document.getElementById("sleek-widget-wrap") && typeof window.$sleek?.close === "function") {
      window.$sleek.close();
    }
  }, [location.pathname]);

  const matchRoute = (pathname: string, path: string) => {
    const regex = new RegExp("^/?" + path.replaceAll(/:[^\s/]+/g, String.raw`([\w-]+)`) + "$");
    return regex.test(pathname);
  };

  useEffect(() => {
    const currentRoute = routes.find((route: (typeof routes)[number]) =>
      route.path.some((path: string) => matchRoute(location.pathname, path)),
    );
    if (currentRoute?.showStickySidebar === false) {
      dispatch(setShowStickySidebar(false));
      dispatch(setSidebarSelectedCell(""));
    }
    if (currentRoute?.showHeader === false) {
      dispatch(setRenderHeader(false));
    } else {
      dispatch(setRenderHeader(true));
    }
    if (location.pathname === "/" && _.get(checkIfAuthenticated, "data.status")) {
      return;
    }
    if (
      location.pathname !== "/" &&
      checkIfAuthenticated !== null &&
      !_.get(checkIfAuthenticated, "data.status") &&
      !location.pathname.includes("thank-you") &&
      !location.pathname.includes("googleSigninButton") &&
      !location.pathname.includes("googleLoginCallback") &&
      !location.pathname.includes("resetPassword") &&
      !location.pathname.includes("95789") &&
      !location.pathname.includes("signup") &&
      !location.pathname.includes("invitation") &&
      !location.pathname.includes("pdfExport") &&
      !location.pathname.includes("newSignIn") &&
      !location.pathname.includes("reset-password") &&
      !location.pathname.includes("newInvitation") &&
      // Shareable external links (CP-283): the recipient has no CUBE account by design, so the
      // generic bounce-to-signin would replace the share with a login screen. The page owns its own
      // no-session states (password gate / invalid link).
      !location.pathname.startsWith("/share/") &&
      // LinkingAccount owns its no-session state (shows Invalid URL per spec)
      // instead of the generic bounce-to-signin.
      !location.pathname.includes(LINKING_ACCOUNT_PATH)
    ) {
      navigate(`/?redirectUrl=${encodeURIComponent(location.pathname + location.search)}${location.hash}`);
    }
  }, [location.pathname, checkIfAuthenticatedStatus, location.hash, location.search]);

  const modulesMapper: Record<string, string> = {
    measurements: "https://support.cube.ms/ebd//22dde45767ab8195b034c62e74458b73",
    general: "https://support.cube.ms/ebd//253de45767ab80f49c72d6992a5610e2?v=266de45767ab802ba907000cc6841787",
  };

  useEffect(() => {
    // if (helpSectionState.display) {
    //   setSplitterSize([70, 30]);
    // } else {
    //   setSplitterSize([100, 0]);
    // }
    if (helpSectionState?.display !== null) {
      const url = modulesMapper[helpSectionState.module] || modulesMapper["general"];
      window.open(url, "_blank");
    }
  }, [helpSectionState?.display]);

  // const handleHelpSectionClick = () => {
  //   dispatch(setHelpSectionState({ display: !helpSectionState.display, module: "general" }));
  // };

  const getContent = () => {
    const projectId = searchParams.get("projectId");
    const routesContent = (
      <ErrorBoundary location={location}>
        <Routes>
          {/* KNOWN BUG, preserved verbatim: `singlePath` is a string out of the route's `path[]`, so
              `singlePath.props` is always undefined. The two routes that declare `props`
              (`/signup` and `/invitation`, both `props: { formType }`) therefore never receive them
              — the read should be off the route entry, not the path string. Left as-is; fixing it
              would change behaviour. */}
          {routes.flatMap(({ path, element }: (typeof routes)[number]) =>
            path.map((singlePath: string) => (
              <Route
                key={singlePath}
                path={singlePath}
                element={
                  <Suspense fallback={<></>}>
                    {/* `element` is the union of every route's component, whose prop types are
                        mutually incompatible, so `createElement` cannot pick an overload; the
                        intersection names a component-with-open-props without widening `element`. */}
                    {createElement(
                      element as typeof element & React.ComponentType<Record<string, unknown>>,
                      (singlePath as string & { props?: Record<string, unknown> }).props,
                    )}
                  </Suspense>
                }
              />
            )),
          )}
        </Routes>
      </ErrorBoundary>
    );

    if (location.pathname.includes("pdfExport")) {
      return (projectId && projectData._id) || !projectId ? (
        routesContent
      ) : projectData.isDataFetched ? (
        <Forbidden />
      ) : (
        <div className="h-full flex justify-center w-full items-center">
          <CircularProgress />
        </div>
      );
    }

    return (
      <SplitLayout
        initialSizes={[100, 0]}
        gutterWidth={"0.125rem"}
        minSizes={[10, 20]}
        style={{ display: "flex", height: "100%" }}
        showProperties={true}
        // onDragStart={() => setIsDragging(true)}
        // onDragEnd={() => setIsDragging(false)}
      >
        {[
          <div key={"mainContent"} className="flex flex-col overflow-auto h-full w-full">
            {renderHeader && (
              <Suspense fallback={<></>}>
                <Header />
              </Suspense>
            )}
            {(projectId && projectData._id) || !projectId ? (
              <div className="flex overflow-auto h-full w-full">
                {showStickySidebar && <StickySidebar userData={undefined} />}
                <div className="w-full h-full overflow-auto">{routesContent}</div>
              </div>
            ) : projectData.isDataFetched ? (
              <Forbidden />
            ) : (
              <div className="h-full flex justify-center w-full items-center">
                <CircularProgress />
              </div>
            )}
          </div>,
          // helpSectionState.display && (
          //   <HelpSection isDragging={isDragging} handleHelpSectionClick={handleHelpSectionClick} module="general" />
          // ),
        ].filter(Boolean)}
      </SplitLayout>
    );
  };
  return (
    <>
      {showLogoutError && (
        <div className="flex justify-around text-white p-1 bg-system_colour_3">
          <Text>{showLogoutError}</Text>
        </div>
      )}
      {!location.pathname.includes("pdfExport") && (
        <Suspense fallback={<></>}>
          <Suspense fallback={<></>}>
            <FeedbackToastComponent />
          </Suspense>
        </Suspense>
      )}

      {showOldBrowserWarning && (
        <Suspense fallback={<></>}>
          <OldBrowserWarning />
        </Suspense>
      )}

      {isMobileDevice && (
        <Suspense fallback={<></>}>
          <MobileDeviceRestrict />
        </Suspense>
      )}

      <Suspense fallback={<></>}>
        {userDetails.id && portalData._id && (
          <Suspense fallback={<></>}>
            <FileSync />
          </Suspense>
        )}
      </Suspense>
      {/* `h-dvh`, not `h-screen`: on a real phone 100vh is the height with the browser's bars HIDDEN, so
          while they show, the bottom of a full-height page (the PDF toolbar) sits behind them — and the
          viewer's stylesheet turns body scrolling off, so it cannot be reached. Identical on desktop. */}
      <div className={`${!location.pathname.includes("pdfExport") ? "flex h-dvh flex-col overflow-auto" : ""}`}>
        {!licenceStatus && <LicenceWarning />}
        <MinimizeFloatter />
        {userDetails.id && portalData._id && <Notification />}
        {userDetails.id &&
          portalData._id &&
          process.env.REACT_APP_ENV &&
          ["qa", "development", "staging"].includes(process.env.REACT_APP_ENV) &&
          !location.pathname.startsWith("/ai/chat") &&
          !location.pathname.includes("pdfExport") &&
          // Never on a share link: a logged-in user who opens one still gets the RECIPIENT
          // experience (QA r3 3b), and CUBE AI answers over the portal, not the shared item.
          !location.pathname.startsWith("/share/") && (
            <Suspense fallback={<></>}>
              <HelpChat />
            </Suspense>
          )}
        {(fileUploadState == "MAXIMISED" || fileUploadState == "MINIMISED") && (
          <Suspense fallback={<></>}>
            <FileUpload />
          </Suspense>
        )}
        {!isPortalExpired && getContent()}
        {isPortalExpired && (
          <Suspense fallback={<></>}>
            <Suspense fallback={<></>}>
              <PortalIllustration />
            </Suspense>
          </Suspense>
        )}
      </div>
    </>
  );
}

export default Router;
